import {
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
    type MouseEvent as ReactMouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export interface SCUIImagePreviewModalProps {
    imageUrl: string | null;
    onClose: () => void;
    alt?: string;
    minZoom?: number;
    maxZoom?: number;
    zoomStep?: number;
}

const DEFAULT_MIN_ZOOM = 1;
const DEFAULT_MAX_ZOOM = 8;
const DEFAULT_ZOOM_STEP = 0.5;

export const SCUIImagePreviewModal: FC<SCUIImagePreviewModalProps> = ({
    imageUrl,
    onClose,
    alt = 'Preview',
    minZoom = DEFAULT_MIN_ZOOM,
    maxZoom = DEFAULT_MAX_ZOOM,
    zoomStep = DEFAULT_ZOOM_STEP,
}) => {
    const { t } = useTranslation();
    const [zoom, setZoom] = useState(minZoom);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
    const didDragRef = useRef(false);
    const zoomConfigRef = useRef({ minZoom, maxZoom, zoomStep });

    zoomConfigRef.current = { minZoom, maxZoom, zoomStep };

    const resetView = useCallback(() => {
        setZoom(minZoom);
        setPan({ x: 0, y: 0 });
        setIsDragging(false);
        didDragRef.current = false;
    }, [minZoom]);

    useEffect(() => {
        resetView();
    }, [imageUrl, resetView]);

    const clampZoom = useCallback(
        (value: number) => Math.min(maxZoom, Math.max(minZoom, Number(value.toFixed(2)))),
        [maxZoom, minZoom],
    );

    const applyZoom = useCallback(
        (nextZoom: number) => {
            const clamped = clampZoom(nextZoom);
            setZoom(clamped);
            if (clamped <= minZoom) {
                setPan({ x: 0, y: 0 });
            }
        },
        [clampZoom, minZoom],
    );

    // Modal açıkken document üzerinde dinle — Dialog portal/ref timing sorununu bypass eder.
    useEffect(() => {
        if (!imageUrl) {
            return;
        }

        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            event.stopPropagation();

            const config = zoomConfigRef.current;
            const direction = event.deltaY < 0 ? 1 : -1;

            setZoom((current) => {
                const next = Math.min(
                    config.maxZoom,
                    Math.max(config.minZoom, Number((current + direction * config.zoomStep).toFixed(2))),
                );
                if (next <= config.minZoom) {
                    setPan({ x: 0, y: 0 });
                }
                return next;
            });
        };

        document.addEventListener('wheel', onWheel, { passive: false, capture: true });
        return () => document.removeEventListener('wheel', onWheel, { capture: true });
    }, [imageUrl]);

    const handleZoomIn = () => applyZoom(zoom + zoomStep);
    const handleZoomOut = () => applyZoom(zoom - zoomStep);

    const handleClose = () => {
        resetView();
        onClose();
    };

    const handleViewportClick = (event: ReactMouseEvent<HTMLDivElement>) => {
        // Sadece boş alana tıklanınca kapat; görsel / kontroller kapamasın.
        if (event.target !== event.currentTarget) {
            return;
        }
        if (didDragRef.current) {
            return;
        }
        handleClose();
    };

    const handleMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
        // Boş alana basıldığında sürükleme başlatma.
        if (event.target === event.currentTarget) {
            didDragRef.current = false;
            return;
        }

        if (zoom <= minZoom) {
            return;
        }

        setIsDragging(true);
        didDragRef.current = false;
        dragStartRef.current = {
            x: event.clientX,
            y: event.clientY,
            panX: pan.x,
            panY: pan.y,
        };
        event.preventDefault();
    };

    const handleMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!isDragging) {
            return;
        }

        const dx = event.clientX - dragStartRef.current.x;
        const dy = event.clientY - dragStartRef.current.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
            didDragRef.current = true;
        }

        setPan({
            x: dragStartRef.current.panX + dx,
            y: dragStartRef.current.panY + dy,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleImageDoubleClick = () => {
        if (didDragRef.current) {
            return;
        }

        if (zoom > minZoom) {
            resetView();
            return;
        }

        applyZoom(Math.min(maxZoom, 3));
    };

    const zoomPercent = Math.round(zoom * 100);
    const canZoomOut = zoom > minZoom;
    const canZoomIn = zoom < maxZoom;
    let viewportCursor: 'default' | 'grab' | 'grabbing' = 'default';
    if (zoom > minZoom) {
        viewportCursor = isDragging ? 'grabbing' : 'grab';
    }

    return (
        <Dialog
            open={!!imageUrl}
            onClose={handleClose}
            maxWidth={false}
            slotProps={{
                backdrop: {
                    sx: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.72)' },
                },
                paper: {
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'visible',
                        m: 0,
                        maxWidth: 'none',
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                    },
                },
            }}
        >
            <Box
                onClick={handleViewportClick}
                sx={{
                    position: 'relative',
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    outline: 'none',
                    cursor: 'default',
                }}
            >
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        handleClose();
                    }}
                    aria-label={t('general.close')}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 3,
                        color: 'white',
                        bgcolor: 'rgba(0,0,0,0.55)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Box
                    onClick={handleViewportClick}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        cursor: viewportCursor,
                        userSelect: 'none',
                    }}
                >
                    {imageUrl && (
                        <Box
                            component="img"
                            src={imageUrl}
                            alt={alt}
                            draggable={false}
                            onClick={(event) => event.stopPropagation()}
                            onDoubleClick={handleImageDoubleClick}
                            sx={{
                                maxWidth: 'min(92vw, 1400px)',
                                maxHeight: '78vh',
                                objectFit: 'contain',
                                borderRadius: 1,
                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                transformOrigin: 'center center',
                                transition: isDragging ? 'none' : 'transform 0.18s ease',
                                boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
                                bgcolor: 'rgba(255,255,255,0.04)',
                            }}
                        />
                    )}
                </Box>

                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 24,
                        transform: 'translateX(-50%)',
                        zIndex: 3,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 999,
                        bgcolor: 'rgba(15, 23, 42, 0.88)',
                        boxShadow: 4,
                    }}
                >
                    <Tooltip title={t('studio.zoomOut')} arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleZoomOut}
                                disabled={!canZoomOut}
                                aria-label={t('studio.zoomOut')}
                                sx={{
                                    color: 'white',
                                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
                                }}
                            >
                                <RemoveIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Typography
                        variant="body2"
                        sx={{
                            minWidth: 56,
                            textAlign: 'center',
                            color: 'white',
                            fontWeight: 700,
                            userSelect: 'none',
                        }}
                    >
                        {zoomPercent}%
                    </Typography>

                    <Tooltip title={t('studio.zoomIn')} arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleZoomIn}
                                disabled={!canZoomIn}
                                aria-label={t('studio.zoomIn')}
                                sx={{
                                    color: 'white',
                                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
                                }}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={t('studio.zoomReset')} arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={resetView}
                                disabled={zoom === minZoom && pan.x === 0 && pan.y === 0}
                                aria-label={t('studio.zoomReset')}
                                sx={{
                                    color: 'white',
                                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
                                }}
                            >
                                <RestartAltIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Box>
        </Dialog>
    );
};
